import { FileTextOutlined } from "@ant-design/icons"
import { App, Col, DatePicker, Row, Select, Typography } from "antd"
import dayjs from "dayjs"
import React from "react"
import { useTranslation } from "react-i18next"

import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import { useDateFormatter } from "../../../../hooks/use-date-formatter.tsx"
import { ArchitectNameField } from "../../../../components/Shared/ArchitectNameField.tsx"
import { ProjectTypeSelect } from "../../../../components/Shared/ProjectTypeSelect.tsx"
import { TextArea } from "../../../../components/Input/TextArea.tsx"
import { TextInput } from "../../../../components/Input/TextInput.tsx"
import { LeadPill } from "../../../Leads/components/LeadPill.tsx"
import {
  LEAD_SOURCE_NONE,
  withNoneSourceOption
} from "../../../Leads/constants/lead-source-none.ts"
import {
  architectNameClearPatch,
  isLeadSourceArchitect
} from "../../../Lead/constants/lead-source-architect.ts"
import { WorkspaceCard } from "../../../Lead/LeadWorkspacePage/cards/WorkspaceCard.tsx"
import { LeadOverviewField } from "../../../Lead/LeadWorkspacePage/overview/LeadOverviewField.tsx"
import { OverviewCardTitle } from "../../../Lead/LeadWorkspacePage/overview/OverviewCardIcon.tsx"
import { diffProjectTypeSelection } from "../../helpers/diff-project-types.ts"
import { typePillStyle } from "../../helpers/project-type-pill-accents.ts"
import { PROJECT_ENTITY_CATEGORIES } from "../../ProjectViewPage/constants/project-entity-category.ts"
import type { Project, ProjectTypeFormData } from "../../types/project.types.ts"
import { typeLabel } from "../../helpers/repeated-project-type.ts"

interface ProjectInfoCardValues {
  lead_name: string
  project_type_ids: string[]
  start_date: string | null
  end_date: string | null
  source: string | null
  architect_name: string | null
  comments: string | null
  project_creation_date: string | null
}

interface AsyncActionResult {
  unwrap: () => Promise<unknown>
}

interface ProjectInformationCardProps {
  project: Project
  filterOptions: Record<string, any>
  canEdit?: boolean
  onSave: (
    changed: Partial<Omit<ProjectInfoCardValues, "project_type_ids">>
  ) => Promise<boolean>
  addProjectType: (args: {
    projectId: string
    data: ProjectTypeFormData
  }) => AsyncActionResult
  removeProjectType: (args: {
    projectId: string
    project_type_id: string
    assignment_id?: string | null
  }) => AsyncActionResult
}

// start_date/end_date are calendar dates, not instants — formatted straight
// off dayjs with no timezone conversion, or a date near midnight in the
// account's zone would silently render as the wrong day.
const formatCalendarDate = (
  value: string | null | undefined,
  format: string
) => (value ? dayjs(value).format(format) : null)

function ProjectInformationCard({
  project,
  filterOptions,
  canEdit,
  onSave,
  addProjectType,
  removeProjectType
}: ProjectInformationCardProps) {
  const { t } = useTranslation()
  const { dateFormat } = useDateFormatter()
  const { modal } = App.useApp()
  const canEditCreationDate = useAppSelector(
    (state) =>
      !!state.auth?.permissions?.codes?.projects?.projects
        ?.project_creation_date_edit
  )

  const sources = withNoneSourceOption(filterOptions?.source)
  const sourceValue = project?.source || LEAD_SOURCE_NONE
  const sourceLabel =
    sources.find((option) => option?.value === sourceValue)?.label ??
    sourceValue

  const typeDetails = project?.project_type_details ?? []
  const typeNames = typeDetails.map(typeLabel)

  // Undeduped, the next save reads the Select's own collapsed value back as a
  // removal.
  const uniqueTypeIds = [...new Set(typeDetails.map((type) => type.id))]
  const typeInitialOptions = uniqueTypeIds.map((id) => ({
    value: id,
    label: typeDetails.find((type) => type.id === id)?.name ?? id
  }))

  const values: ProjectInfoCardValues = {
    lead_name: project?.lead_name ?? "",
    project_type_ids: uniqueTypeIds,
    start_date: project?.start_date ?? null,
    end_date: project?.end_date ?? null,
    project_creation_date: project?.project_creation_date ?? null,
    source: sourceValue,
    architect_name: project?.architect_name ?? null,
    comments: project?.comments ?? null
  }

  const instancesOf = (project_type_id: string) =>
    typeDetails.filter((type) => type.id === project_type_id)

  // Project Type doesn't PATCH like every other field here — add/remove each
  // go through their own dedicated endpoints (the same ones the old giant
  // form's submit used), and removing a type deletes its milestones/finance
  // items server-side, so it keeps the same confirm step that guarded that
  // before.
  const applyTypeChanges = async (addedIds: string[], removedIds: string[]) => {
    for (const project_type_id of addedIds) {
      await addProjectType({
        projectId: project.id,
        data: {
          project_type_id,
          category: PROJECT_ENTITY_CATEGORIES.STANDARD
        }
      }).unwrap()
    }
    for (const project_type_id of removedIds) {
      await removeProjectType({
        projectId: project.id,
        project_type_id,
        assignment_id: instancesOf(project_type_id)[0]?.assignment_id
      }).unwrap()
    }
  }

  const handleSave = async (changed: Partial<ProjectInfoCardValues>) => {
    const { project_type_ids, ...rest } = changed

    if (!project_type_ids) {
      if (!Object.keys(rest).length) return true
      return onSave(rest)
    }

    const { addedIds, removedIds } = diffProjectTypeSelection(
      values.project_type_ids,
      project_type_ids
    )

    // One chip covers every instance, so deselecting it names none of them.
    const repeated = removedIds
      .map((id) => instancesOf(id))
      .find((instances) => instances.length > 1)

    if (repeated) {
      modal.info({
        title: t("projectTypeRemove.title"),
        content: t("projectTypeRepeat.removeRepeated", {
          name: repeated[0]?.name ?? ""
        })
      })
      return false
    }

    const finish = async () => {
      try {
        await applyTypeChanges(addedIds, removedIds)
      } catch (e) {
        console.error("Update project type error:", e)
        return false
      }
      if (!Object.keys(rest).length) return true
      return onSave(rest)
    }

    if (!removedIds.length) return finish()

    return new Promise<boolean>((resolve) => {
      modal.confirm({
        title: t("projectTypeRemove.title"),
        content: (
          <>
            <p>{t("projectTypeRemove.lede", { count: removedIds.length })}</p>
            <p>{t("projectTypeRemove.consequence")}</p>
          </>
        ),
        okText: t("projectTypeRemove.confirm"),
        okType: "danger",
        cancelText: t("projectTypeRemove.cancel"),
        onOk: async () => resolve(await finish()),
        onCancel: () => resolve(false)
      })
    })
  }

  return (
    <WorkspaceCard<ProjectInfoCardValues>
      cardId={"project-information"}
      title={
        <OverviewCardTitle icon={<FileTextOutlined />} accent={"orange"}>
          {t("projectWorkspace.cards.info.title")}
        </OverviewCardTitle>
      }
      ariaTitle={t("projectWorkspace.cards.info.title")}
      className={"h-full"}
      canEdit={canEdit}
      values={values}
      onSave={handleSave}
      validate={({ lead_name, project_type_ids, start_date, end_date }) =>
        !!lead_name?.trim() &&
        project_type_ids.length > 0 &&
        (!start_date ||
          !end_date ||
          !dayjs(end_date).isBefore(dayjs(start_date), "day"))
      }
      renderEdit={({ values, setValues, saving }) => (
        <Row gutter={[12, 12]}>
          <Col xs={24}>
            <LeadOverviewField label={t("projectWorkspace.cards.info.name")}>
              <TextInput
                autoFocus
                disabled={saving}
                aria-label={t("projectWorkspace.cards.info.name")}
                value={values.lead_name}
                onChange={(event) =>
                  setValues({ lead_name: event.target.value })
                }
              />
            </LeadOverviewField>
          </Col>
          <Col xs={24}>
            <LeadOverviewField label={t("projectWorkspace.cards.info.types")}>
              <ProjectTypeSelect
                disabled={saving}
                aria-label={t("projectWorkspace.cards.info.types")}
                initialOptions={typeInitialOptions}
                value={values.project_type_ids}
                onChange={(ids) => setValues({ project_type_ids: ids })}
              />
            </LeadOverviewField>
          </Col>
          <Col xs={24} sm={12}>
            <LeadOverviewField
              label={t("projectWorkspace.cards.info.startDate")}
            >
              <DatePicker
                className={"w-full"}
                disabled={saving}
                format={dateFormat}
                aria-label={t("projectWorkspace.cards.info.startDate")}
                value={values.start_date ? dayjs(values.start_date) : null}
                onChange={(date) =>
                  setValues({
                    start_date: date ? date.format("YYYY-MM-DD") : null
                  })
                }
              />
            </LeadOverviewField>
          </Col>
          <Col xs={24} sm={12}>
            <LeadOverviewField label={t("projectWorkspace.cards.info.endDate")}>
              <DatePicker
                className={"w-full"}
                disabled={saving}
                format={dateFormat}
                aria-label={t("projectWorkspace.cards.info.endDate")}
                value={values.end_date ? dayjs(values.end_date) : null}
                onChange={(date) =>
                  setValues({
                    end_date: date ? date.format("YYYY-MM-DD") : null
                  })
                }
              />
            </LeadOverviewField>
          </Col>
          <Col xs={24} sm={12}>
            <LeadOverviewField label={t("projectWorkspace.cards.info.source")}>
              <Select
                allowClear
                className={"w-full"}
                disabled={saving}
                aria-label={t("projectWorkspace.cards.info.source")}
                optionFilterProp={"label"}
                options={sources}
                value={values.source ?? undefined}
                onChange={(next) =>
                  setValues({
                    source: next ?? LEAD_SOURCE_NONE,
                    ...architectNameClearPatch(next)
                  })
                }
              />
            </LeadOverviewField>
          </Col>
          {isLeadSourceArchitect(values.source) && (
            <Col xs={24} sm={12}>
              <ArchitectNameField
                disabled={saving}
                value={values.architect_name}
                onChange={(architect_name) => setValues({ architect_name })}
              />
            </Col>
          )}
          <Col xs={24} sm={12}>
            <LeadOverviewField
              label={t("projectWorkspace.cards.info.creationDate")}
            >
              {canEditCreationDate ? (
                <DatePicker
                  className={"w-full"}
                  disabled={saving}
                  format={dateFormat}
                  aria-label={t("projectWorkspace.cards.info.creationDate")}
                  value={
                    values.project_creation_date
                      ? dayjs(values.project_creation_date)
                      : null
                  }
                  onChange={(date) =>
                    setValues({
                      project_creation_date: date
                        ? date.format("YYYY-MM-DD")
                        : null
                    })
                  }
                />
              ) : (
                <Typography.Text type={"secondary"}>
                  {values.project_creation_date
                    ? dayjs(values.project_creation_date).format(dateFormat)
                    : t("projectWorkspace.cards.info.creationDateNotSet")}
                </Typography.Text>
              )}
            </LeadOverviewField>
          </Col>
          <Col xs={24}>
            <LeadOverviewField
              label={t("projectWorkspace.cards.info.description")}
            >
              <TextArea
                rows={3}
                disabled={saving}
                aria-label={t("projectWorkspace.cards.info.description")}
                value={values.comments ?? ""}
                onChange={(event) =>
                  setValues({ comments: event.target.value || null })
                }
              />
            </LeadOverviewField>
          </Col>
        </Row>
      )}
    >
      <Row gutter={[16, 14]}>
        <Col xs={24} sm={12}>
          <LeadOverviewField
            label={t("projectWorkspace.cards.info.name")}
            value={project?.lead_name}
          />
        </Col>
        <Col xs={24} sm={12}>
          <LeadOverviewField
            label={t("projectWorkspace.cards.info.types")}
            value={typeNames}
          >
            <span>
              {typeNames.map((name, index) => (
                <LeadPill
                  key={name}
                  style={typePillStyle(index)}
                  className={"mr-1 mb-1"}
                >
                  {name}
                </LeadPill>
              ))}
            </span>
          </LeadOverviewField>
        </Col>
        <Col xs={24} sm={12}>
          <LeadOverviewField
            label={t("projectWorkspace.cards.info.startDate")}
            value={formatCalendarDate(project?.start_date, dateFormat)}
          />
        </Col>
        <Col xs={24} sm={12}>
          <LeadOverviewField
            label={t("projectWorkspace.cards.info.endDate")}
            value={formatCalendarDate(project?.end_date, dateFormat)}
          />
        </Col>
        <Col xs={24} sm={12}>
          <LeadOverviewField
            label={t("projectWorkspace.cards.info.creationDate")}
            value={formatCalendarDate(
              project?.project_creation_date,
              dateFormat
            )}
          />
        </Col>
        <Col xs={24} sm={12}>
          <LeadOverviewField
            label={t("projectWorkspace.cards.info.source")}
            value={sourceLabel}
          />
        </Col>
        {isLeadSourceArchitect(sourceValue) && (
          <Col xs={24} sm={12}>
            <LeadOverviewField
              label={t("leadWizard.project.architectName")}
              value={project?.architect_name}
            />
          </Col>
        )}
        <Col xs={24}>
          <LeadOverviewField
            label={t("projectWorkspace.cards.info.description")}
            value={project?.comments}
          />
        </Col>
      </Row>
    </WorkspaceCard>
  )
}

export { type ProjectInfoCardValues, ProjectInformationCard }
